<script setup lang="ts">
import { useApiStore } from "@/stores/api/useApiStore";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";

const props = defineProps<{
  url: string;
}>();

const apiStore = useApiStore();
const { apiEntries } = storeToRefs(apiStore);

const expanded = ref(false);

const apiEntry = computed(() => apiEntries.value.get(props.url));
const schemaInfo = computed(() => apiEntry.value?.schemaInfo);
const failedEndpoints = computed(() => apiEntry.value?.failedEndpoints ?? []);
const registrableFunctions = computed(() => apiEntry.value?.registrableFunctions ?? []);
const nonRegistrableFunctions = computed(() => apiEntry.value?.nonRegistrableFunctions ?? []);

const hasPartialWarnings = computed(() => failedEndpoints.value.length > 0 || nonRegistrableFunctions.value.length > 0);
</script>

<template>
  <v-card elevation="0">
    <v-card-item>
      <template #title>
        <span class="d-flex align-center ga-2 equation-type-title">
          <v-icon v-if="hasPartialWarnings" size="small" color="warning">mdi-alert-circle-outline</v-icon>
          {{ schemaInfo?.title }}
        </span>
      </template>
      <template #append>
        <v-btn
          v-if="hasPartialWarnings"
          :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          variant="text"
          density="comfortable"
          size="small"
          @click="expanded = !expanded"
        />
      </template>
    </v-card-item>

    <v-card-text class="pt-0">
      <p class="mb-2 equation-type-body">{{ schemaInfo?.description }}</p>

      <div v-if="schemaInfo?.contact" class="mb-1 equation-type-body">
        <span><strong>Contact:</strong></span>
        <a
          v-if="schemaInfo.contact.email"
          :href="`mailto:${schemaInfo.contact.email}`"
          class="schema-info-card__contact-link d-inline-flex align-center ga-1 rounded text-primary font-weight-semibold text-decoration-none"
          style="padding: 1px 4px"
        >
          {{ schemaInfo.contact.name }}
          <v-icon size="14">mdi-email-outline</v-icon>
        </a>
        <span v-else>{{ schemaInfo.contact.name }}</span>
      </div>

      <div v-if="schemaInfo?.version" class="mb-2 equation-type-body">
        <span> <strong>Version:</strong> {{ schemaInfo.version }} </span>
      </div>

      <v-alert
        v-if="registrableFunctions.length > 0"
        class="mb-2"
        type="success"
        color="success"
        variant="tonal"
        density="compact"
      >
        <span class="equation-type-body">
          Registered <strong>{{ registrableFunctions.length }}</strong> operations successfully.
        </span>
      </v-alert>

      <v-btn
        v-if="hasPartialWarnings && !expanded"
        variant="text"
        density="compact"
        size="small"
        @click="expanded = true"
      >
        <v-icon start size="small">mdi-chevron-down</v-icon>
        Show details
      </v-btn>

      <v-expand-transition>
        <div v-if="expanded">
          <v-alert
            v-if="failedEndpoints.length > 0"
            class="mb-2"
            type="warning"
            color="warning"
            variant="tonal"
            density="compact"
          >
            <span class="equation-type-body">
              <strong>{{ failedEndpoints.length }}</strong> operations could not be loaded from API.
            </span>
            <p class="mt-1 mb-0 equation-type-label">
              {{ failedEndpoints.join(", ") }}
            </p>
          </v-alert>

          <v-alert
            v-if="nonRegistrableFunctions.length > 0"
            class="mb-2"
            type="warning"
            color="warning"
            variant="tonal"
            density="compact"
          >
            <span class="equation-type-body">
              <strong>{{ nonRegistrableFunctions.length }}</strong> operations failed Excel registration.
            </span>
            <p class="mt-1 mb-0 equation-type-label">
              {{ nonRegistrableFunctions.map((func) => func.id).join(", ") }}
            </p>
          </v-alert>

          <v-btn variant="text" density="compact" size="small" @click="expanded = false">
            <v-icon start size="small">mdi-chevron-up</v-icon>
            Hide details
          </v-btn>
        </div>
      </v-expand-transition>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.schema-info-card__contact-link:hover {
  background: rgba(var(--v-theme-primary), 0.08);
}
</style>
